/**
 * R3-A OPTION B (founder decision 2026-10-21) — the browser's door to the SERVER's Run Long AutoBump.
 *
 * Run Long AutoBump (a running job's finish keeps pace with the clock; the work behind it cascades) now runs on the
 * server (Edge function `run-long-autobump`, every minute from the one pg_cron job, and on request). A Dayboard asks
 * it to reconcile NOW — on open, on reconnect, and on its own 60 s tick — instead of computing and requesting the
 * writes itself. The server answers:
 *
 *   RECONCILED           it ran the reconcile for this request
 *   RECONCILED_BY_OTHER  another caller (the cron, another board) was reconciling; it finished while we waited
 *   FRESH                the shop was reconciled less than 10 s ago (nothing to do)
 *   BUSY_TIMEOUT         another caller was still reconciling after 10 s
 *   PARTIAL              (oct20c) the server ran, but some lanes / actions / claimed chain growth did not finish; it
 *                        recorded which, and every later run (the cron, every minute) retries them
 *   FAILED               (oct20c) the server could not reconcile at all this time (the shop could not be read, every
 *                        lane failed, or its run died) — it wrote nothing it could vouch for
 *
 * When the server owns the writes (RECONCILED / RECONCILED_BY_OTHER / FRESH / PARTIAL) the hooks run NOTICE-ONLY; on
 * FAILED, BUSY_TIMEOUT or any error (an older server without the function, offline) they fall back to their own
 * requests exactly as before.
 *
 * Why PARTIAL stays server-owned (oct20c decision): the server loaded the shop, applied what it could, holds the
 * durable pending-growth ledger and retries the failed lanes on its next run (≤ 1 minute, the lanes it failed are
 * walked again even if nothing moves). A browser writing beside it would re-open the race the ledger closes (two
 * writers growing one chain) and duplicate writes the server already made. The board does not pretend: it shows
 * "could not be fully updated — retrying". Why FAILED falls back: the server vouched for nothing this run (often it
 * could not even read the shop), so the open board does what it did before Option B — its own requests through the
 * same CORE-AC authorities (ticket-finish-intent, compare-and-swap) — and the banner still says the server is failing.
 *
 * One request per shop at a time (in-flight dedupe), and a recent answer can be reused (`maxAgeMs`) so the
 * pushback hook, 20 s behind AutoBump, does not ask again.
 */
import { supabase } from '@/integrations/supabase/client';
import { clock } from '@/utils/clock';

export type RunLongReconcileOutcome = 'RECONCILED' | 'RECONCILED_BY_OTHER' | 'FRESH' | 'BUSY_TIMEOUT' | 'PARTIAL' | 'FAILED' | 'ERROR';

export interface RunLongReconcileResult {
  outcome: RunLongReconcileOutcome;
  /** When the server last completed a reconcile of this shop (ISO), when it said. */
  reconciledAt: string | null;
  /** Client clock when this answer arrived. */
  receivedAtMs: number;
  /** RECONCILED only: how many timing writes the server applied for this request. */
  applied?: number;
  /** oct20c PARTIAL / FAILED: the technicians whose lanes did not finish, and claimed chain growth still pending. */
  failedLanes?: string[];
  pendingGrowth?: number | null;
  error?: string;
}

const SERVER_OWNS = new Set<RunLongReconcileOutcome>(['RECONCILED', 'RECONCILED_BY_OTHER', 'FRESH', 'PARTIAL']);
const KNOWN = ['RECONCILED', 'RECONCILED_BY_OTHER', 'FRESH', 'BUSY_TIMEOUT', 'PARTIAL', 'FAILED'];

/** oct20c: the answer says the server's schedule is NOT fully reconciled (the board must say so). */
export function reconcileIncomplete(r: RunLongReconcileResult | null | undefined): boolean {
  return !!r && (r.outcome === 'PARTIAL' || r.outcome === 'FAILED');
}

/** Did the server take the run-long writes for this tick? (Then the hooks only notify.) */
export function serverOwnsRunLong(r: RunLongReconcileResult | null | undefined): boolean {
  return !!r && SERVER_OWNS.has(r.outcome);
}

type Invoke = (shopId: string, force: boolean) => Promise<{ data: unknown; error: unknown }>;

const defaultInvoke: Invoke = (shopId, force) =>
  supabase.functions.invoke('run-long-autobump', { body: force ? { shopId, force: true } : { shopId } }) as Promise<{ data: unknown; error: unknown }>;

const inFlight = new Map<string, Promise<RunLongReconcileResult>>();
const lastAnswer = new Map<string, RunLongReconcileResult>();

/** Test seam: forget in-flight requests and cached answers. */
export function __resetRunLongReconcileClient(): void {
  inFlight.clear();
  lastAnswer.clear();
}

function parse(data: unknown): Omit<RunLongReconcileResult, 'receivedAtMs'> | null {
  const env = (data ?? {}) as { data?: Record<string, unknown> } & Record<string, unknown>;
  const r = (env.data ?? env) as Record<string, unknown>;
  const outcome = String(r.outcome ?? '');
  if (!KNOWN.includes(outcome)) return null;
  return {
    outcome: outcome as RunLongReconcileOutcome,
    reconciledAt: typeof r.reconciledAt === 'string' ? r.reconciledAt : null,
    ...(typeof r.applied === 'number' ? { applied: r.applied } : {}),
    ...(Array.isArray(r.failedLanes) ? { failedLanes: (r.failedLanes as unknown[]).map(String) } : {}),
    ...(typeof r.pendingGrowth === 'number' ? { pendingGrowth: r.pendingGrowth } : {}),
  };
}

/**
 * Ask the server to reconcile this shop's run-long schedule now. Never throws: any failure answers ERROR.
 * @param opts.force    skip the server's 10 s freshness window (Dayboard open / reconnect). Never skips its lease.
 * @param opts.maxAgeMs reuse an answer that arrived within this many ms (default 0: always ask).
 */
export async function requestRunLongReconcile(
  shopId: string,
  opts: { force?: boolean; maxAgeMs?: number } = {},
  invoke: Invoke = defaultInvoke,
): Promise<RunLongReconcileResult> {
  const now = clock.nowMs();
  const cached = lastAnswer.get(shopId);
  if (!opts.force && cached && (opts.maxAgeMs ?? 0) > 0 && now - cached.receivedAtMs <= (opts.maxAgeMs ?? 0)) return cached;
  const pending = inFlight.get(shopId);
  if (pending) return pending;
  const p = (async (): Promise<RunLongReconcileResult> => {
    await Promise.resolve(); // always asynchronous: the in-flight entry is registered before `finally` can clear it
    try {
      const { data, error } = await invoke(shopId, opts.force === true);
      if (error) {
        return { outcome: 'ERROR', reconciledAt: null, receivedAtMs: clock.nowMs(), error: String((error as { message?: string })?.message ?? error) };
      }
      const parsed = parse(data);
      if (!parsed) return { outcome: 'ERROR', reconciledAt: null, receivedAtMs: clock.nowMs(), error: 'unrecognised answer' };
      const result: RunLongReconcileResult = { ...parsed, receivedAtMs: clock.nowMs() };
      lastAnswer.set(shopId, result);
      return result;
    } catch (e) {
      return { outcome: 'ERROR', reconciledAt: null, receivedAtMs: clock.nowMs(), error: (e as Error)?.message ?? String(e) };
    } finally {
      inFlight.delete(shopId);
    }
  })();
  inFlight.set(shopId, p);
  return p;
}

export interface RunLongReconcileStatus {
  /** The last FULLY successful reconcile (oct20c: a partial / failed run never advances it). */
  lastCompletedAt: string | null;
  inProgress: boolean;
  due: boolean;
  /** oct20c: the last run's outcome (null: never ran / an older server). */
  lastOutcome: 'FULL_SUCCESS' | 'PARTIAL' | 'FAILED' | null;
  lastFailedAt: string | null;
  failedLanes: string[];
  pendingGrowth: number;
}

/** The members' read of the last reconcile (run_long_autobump_status). Null when unreadable. */
export async function readRunLongReconcileStatus(shopId: string): Promise<RunLongReconcileStatus | null> {
  try {
    const { data, error } = await (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>)(
      'run_long_autobump_status', { p_shop: shopId });
    if (error || !data) return null;
    const d = data as { last_completed_at?: string | null; in_progress?: boolean; due?: boolean; last_outcome?: string | null;
      last_failed_at?: string | null; failed_lanes?: unknown; pending_growth?: number; abandoned?: boolean };
    const lo = d.abandoned === true ? 'FAILED' : d.last_outcome;
    // `due`: the shop has live work today (the cron reconciles only those); an older server omits it → assume due.
    return {
      lastCompletedAt: d.last_completed_at ?? null, inProgress: d.in_progress === true, due: d.due !== false,
      lastOutcome: lo === 'FULL_SUCCESS' || lo === 'PARTIAL' || lo === 'FAILED' ? lo : null,
      lastFailedAt: d.last_failed_at ?? null,
      failedLanes: Array.isArray(d.failed_lanes) ? (d.failed_lanes as unknown[]).map(String) : [],
      pendingGrowth: typeof d.pending_growth === 'number' ? d.pending_growth : 0,
    };
  } catch {
    return null;
  }
}
